import React from 'react';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';

import DialogBigTextField from './DialogBigTextField';
import Iconify from '../../components/iconify';
import Label from '../../components/label';
import { AppDataContext } from '../../context/Context';
import i18n from '../../i18n';

// The syntax note: each line a sentence and, where one helps, an example in the
// language itself, which is never translated.
const SYNTAX = [
    ['walletRules.syntax.if', 'if ($count_per_day_wallet%d < 10%d) { true } else { false }'],
    ['walletRules.syntax.operators'],
    ['walletRules.syntax.sum', '$total_amount_per_day_wallet%d + $total_amount_active_wallet%d + $request_amount%d <= 1500000000%d'],
    ['walletRules.syntax.failureSlot', '$count_per_day_wallet_2%d > 5%d'],
    ['walletRules.syntax.dayRange', '$count_30_days_wallet[0+6]%d >= 1%d'],
    ['walletRules.syntax.whitespace'],
    ['walletRules.syntax.case']
];

const codeSx = {
    m: 0,
    p: 1,
    borderRadius: 1,
    bgcolor: 'background.neutral',
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all'
};

/**
 * A wallet rule's filterRule: the BigText editor, and under it the language's help
 * behind a toggle — a short syntax note and every variable a rule may read, as the
 * API lists them (WalletRulesPage loads them into the store): its name as written
 * in a rule, the kinds of rule that may read it, whether it takes `_2` (failures)
 * and `[start+count]` (a day range), and what it means.
 *
 * The cabinet compiles the rule on save and refuses one that does not compile
 * (422 `WALLET_RULE_INVALID: …`); the dialog shows that as any other refusal.
 */
export default class DialogWalletRuleField extends React.Component {
    static contextType = AppDataContext;

    /** Where the page put the variables in the store; a subclass for another kind of rule names its own. */
    static variablesKey = 'walletRuleVariables';

    constructor(props) {
        super(props);
        this.state = { showHelp: false, variables: this.variables() };
    }

    componentDidMount() {
        // The page loads the variables as it mounts; a dialog opened first catches
        // up here. Only their arrival re-renders: the store changes often.
        this.unsubscribe = this.store()?.subscribe?.(() => {
            const variables = this.variables();
            if (variables !== this.state.variables) this.setState({ variables });
        });
    }

    componentWillUnmount() {
        this.unsubscribe?.();
    }

    store() {
        return this.props.context || this.context;
    }

    /** The list, `false` when it could not be loaded, undefined until then. */
    variables() {
        return this.store()?.getState()[this.constructor.variablesKey];
    }

    /** The syntax note's lines: [key, example?]. */
    syntax() {
        return SYNTAX;
    }

    /** The variables the help lists; a subclass may list fewer. */
    listedVariables(variables) {
        return variables;
    }

    toggleHelp = () => {
        this.setState(({ showHelp }) => ({ showHelp: !showHelp }));
    };

    renderVariable = ({ name, type, kinds, failureSlot, dayRange, description }) => (
        <Box key={name}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
                <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>
                    {`$${name}${type || ''}`}
                </Typography>
                {(kinds || []).map((kind) => (
                    <Label key={kind}>{i18n.t(`enums.${kind}`)}</Label>
                ))}
                {failureSlot && (
                    <Label color="info" title={i18n.t('walletRules.failureSlot')}>
                        _2
                    </Label>
                )}
                {dayRange && (
                    <Label color="info" title={i18n.t('walletRules.dayRange')}>
                        [start+count]
                    </Label>
                )}
            </Stack>
            {description && (
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            )}
        </Box>
    );

    renderVariables() {
        const { variables } = this.state;
        if (variables === false) {
            return (
                <Typography variant="body2" color="error">
                    {i18n.t('walletRules.variablesUnavailable')}
                </Typography>
            );
        }
        if (!Array.isArray(variables)) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {i18n.t('walletRules.variablesLoading')}
                </Typography>
            );
        }
        return <Stack spacing={1}>{this.listedVariables(variables).map(this.renderVariable)}</Stack>;
    }

    renderHelp() {
        const { showHelp } = this.state;
        return (
            <Box>
                <Button
                    size="small"
                    onClick={this.toggleHelp}
                    startIcon={<Iconify icon={showHelp ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} />}
                >
                    {i18n.t('walletRules.help')}
                </Button>
                <Collapse in={showHelp} unmountOnExit>
                    <Stack spacing={1.5} sx={{ pt: 1, px: 1 }}>
                        <Stack spacing={0.75}>
                            {this.syntax().map(([key, example]) => (
                                <Box key={key}>
                                    <Typography variant="body2" color="text.secondary">
                                        {i18n.t(key)}
                                    </Typography>
                                    {example && (
                                        <Box component="pre" sx={{ ...codeSx, mt: 0.5 }}>
                                            {example}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                        <Typography variant="subtitle2">{i18n.t('walletRules.variables')}</Typography>
                        <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 1 }}>{this.renderVariables()}</Box>
                    </Stack>
                </Collapse>
            </Box>
        );
    }

    render() {
        return (
            <Stack spacing={1}>
                <DialogBigTextField {...this.props} />
                {this.renderHelp()}
            </Stack>
        );
    }
}
